/* eslint-disable @ngrx/prefix-selectors-with-select */
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { PatientHealthConcern } from '../../../models/patient-health-concern';
import * as fromHealthConcerns from '../reducer/patient-health-concern.reducer';

export const patientHealthConcernsFeatureSelector =
  createFeatureSelector<fromHealthConcerns.PatientHealthConcernsState>(
    fromHealthConcerns.patientHealthConcernsFeatureKey
  );

const { selectEntities, selectAll } = fromHealthConcerns.patientHealthConcernsAdapter.getSelectors();

export const selectPatientHealthConcernEntities = createSelector(patientHealthConcernsFeatureSelector, selectEntities);

export const selectPatientHealthConcerns = createSelector(patientHealthConcernsFeatureSelector, selectAll);

export const selectHasHealthConcerns = createSelector(
  selectPatientHealthConcerns,
  healthConcerns => healthConcerns.length > 0
);

/** Active concerns, preserving the order returned by the API. */
export const selectActiveHealthConcerns = createSelector(selectPatientHealthConcerns, healthConcerns =>
  healthConcerns.filter(concern => concern.isActive)
);

/** Inactive concerns, reverse-chronological by resolved (inactive) date. */
export const selectInactiveHealthConcerns = createSelector(selectPatientHealthConcerns, healthConcerns =>
  healthConcerns.filter(concern => !concern.isActive).sort(compareByInactiveDateDesc)
);

export const selectHealthConcernById = (patientHealthConcernId: number) =>
  createSelector(selectPatientHealthConcernEntities, entities => entities[patientHealthConcernId] ?? null);

export const selectIsLoading = createSelector(
  patientHealthConcernsFeatureSelector,
  state => state.baseState.isLoading ?? false
);

export const selectError = createSelector(patientHealthConcernsFeatureSelector, state => state.baseState.error);

export const selectIsLoaded = createSelector(patientHealthConcernsFeatureSelector, state => state?.isLoaded ?? false);

export const selectCachePatientId = createSelector(
  patientHealthConcernsFeatureSelector,
  state => state?.cachePatientId ?? null
);

export const selectCacheLoadedAt = createSelector(
  patientHealthConcernsFeatureSelector,
  state => state?.cacheLoadedAt ?? null
);

export const selectDetailLoading = createSelector(
  patientHealthConcernsFeatureSelector,
  state => state?.detailLoading ?? false
);

export const selectDetailError = createSelector(
  patientHealthConcernsFeatureSelector,
  state => state?.detailError ?? null
);

function compareByInactiveDateDesc(a: PatientHealthConcern, b: PatientHealthConcern): number {
  const aTime = a.inactiveDate ? new Date(a.inactiveDate).getTime() : 0;
  const bTime = b.inactiveDate ? new Date(b.inactiveDate).getTime() : 0;
  return bTime - aTime;
}
